/**
 *
 * PROJECT eVessel
 * Developed by:  3WebBox LLC - 2023
 * 
 * Disclaimer: Please make sure to read related documentation before
 * making any changes to the code. Modify the code under your own
 * responsibility. for help please contact 3WebBox.
 * 
 * https://3webbox.com  : support@3webbox.com
 * 
 * 
 */

import { useState, useEffect } from 'react';

import { CheckOutlined, DeleteFilled, LeftCircleFilled, PlusCircleFilled, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Form, Input, Menu, message, Pagination, Row, Segmented, Select, Space, Switch, Table, Tabs, Typography } from "antd";

import BlankPage, { LayoutModalProps } from "../../framework/blank_page";
import EVAPI from '../../lib/ev_lib/main';
import { config } from '../../config';
import { useNavigate, useParams } from 'react-router-dom';
import ContactsListing from '../general/contacts_listing';
import { setClearFormFields } from '../../util/clear_empty';
import { URLQuery } from '../../util/url_queries';

export default function TruckingCompanyForm(props:any) {
  interface truckingCompany {
    uuid: string|null,
    company_name: string|null,
    is_active?: 'y'|'n'|null,
    primary_contact_uuid?: string|null,
    clear?:string[]|undefined
  }

  const [messageApi, contextHolder] = message.useMessage();

  const navigate = useNavigate();
  let { uuid } = useParams<any>();
  
  const [form] = Form.useForm();
  
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [authorized, setAuthorized] = useState<boolean>(false);

  const [errors, setErrors] = useState<any[]|null>(null);
  
  const [selectedTab, setSelectedTab] = useState<'contacts'|'consignees'>('contacts');
  const [truckingCompany, setTruckingCompany] = useState<truckingCompany>({
    uuid: uuid||null,
    company_name: null
  });

  const [layoutModal, setLayoutModal] = useState<LayoutModalProps>({open: false});

  let profile:any = localStorage.getItem('profile');

  useEffect(() => {
    try {
      if(typeof profile !== 'object') {
        profile = JSON.parse(profile);
      }
      
      if(profile.type === 'management') {
        setAuthorized(true);
      }
    }
    catch(e) {
      console.warn(e);
    };

    if(uuid) {
      getTruckingCompany();
    }
    
    setIsReady(true);
  }, [])

  const getTruckingCompany = async () => {
    if(isLoading) return null;
    setIsLoading(true);

    var EV:any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window)||localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.uuid = uuid;

    var res:any = await EV.getTruckingCompany();
    
    if(!res) {
      messageApi.open({
        type: 'error',
        content: 'Failed to pull data from server',
      });  
    }

    else if(res.data) {
      setTruckingCompany(res.data);
      form.setFieldsValue(res.data)
    }

    setIsLoading(false);
    return null;
  }

  const processSubmit = async (customValues:any) => {
    if(isLoading) return null;
    setIsLoading(true);

    var EV:any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window)||localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.primary_contact_uuid = customValues?.primary_contact_uuid||truckingCompany.primary_contact_uuid
    
    // handle cleared fields
    if(truckingCompany.uuid) {
      // check clearables
      truckingCompany.clear = setClearFormFields(truckingCompany)||undefined;
    }

    Object.assign(EV, truckingCompany)
    var res:any = null;

    if(truckingCompany.uuid) res = await EV.updateTruckingCompany();
    else res = await EV.createTruckingCompany();

    if(!res) {
      messageApi.open({
        type: 'error',
        content: 'Failed to complete the process',
      });  
    }

    else if(res.status === 'fail') {
      setErrors(res.errors);
    }

    else if(res.code === 200) {
      messageApi.open({
        type: 'success',
        content: 'Process completed',
      });

      if(!truckingCompany.uuid) {
        setTruckingCompany({
          ...truckingCompany,
          uuid: res.data.uuid
        })
        
        navigate('/trucking-companies/form/' + res.data.uuid, { replace: true });
      }
    }

    setIsLoading(false);
    return null;
  }

  const deleteTruckingCompany = async () => {
    if(isDeleting) return;
    setIsDeleting(true);
    
    var EV:any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window)||localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    EV.uuid = truckingCompany?.uuid;
    
    var res:any = await EV.deleteTruckingCompany();
    // var res:any = false;
    
    if(!res) {
      messageApi.open({
        type: 'error',
        content: 'Network error mailing the record',
      });  
    }

    else if(res.status === 'fail') {
      setErrors(res.errors);
    }

    else if(res.code === 200) {
      messageApi.open({
        type: 'success',
        content: 'Process completed',
      });

      navigate(-1);
      return;
    }

    setIsDeleting(false);
    return;
  }

  return <BlankPage
    isLoading={!isReady}
    unauthorized={!authorized}
    modal={layoutModal}
    showSideMenu
    padded
    customHeader={<Space size={'small'}>
      <Button
        type={'default'}
        icon={<LeftCircleFilled />}
        children={'Back'}
        onClick={() => navigate(-1)}
      />
      {(truckingCompany.uuid) && <>
        <Button
          danger
          type={'primary'}
          icon={<DeleteFilled />}
          children={'Delete'}
          onClick={() => setLayoutModal({
            open: true,
            title: 'Are you sure?',
            children: [<>
              <Typography.Text>
                You are about to delete this record. This action
                may not be reversable. Are you sure you want to continue?
              </Typography.Text>
            </>],
            okText: 'Confirm',
            onOk: () => {
              setLayoutModal({open: false});
              deleteTruckingCompany();
            },
            onCancel: () => setLayoutModal({open: false})
          })}
        />
      </>}
    </Space>}
  >
    {contextHolder}

    <Typography.Title 
      level={1}
      children={(truckingCompany?.uuid) ? 'Manage Trucking Company' : 'New Trucking Company'}
    />

    <Typography.Paragraph>
      <Typography.Text type={'secondary'} children={'# '} />
      <Typography.Text
        children={truckingCompany?.uuid||'Unassigned'}
        copyable
        type={'secondary'}
      />
    </Typography.Paragraph>

    {(errors && errors.length > 0) && <Alert
      type={'error'}
      message={'Errors submitting your data'}
      description={<ul>{errors.map((error, key) => {
        if(typeof error === 'object') {
          error = error.en
        }

        return <li>{error}</li>;
      })}</ul>}
      closable
      onClose={() => setErrors(null)}
      style={{marginBottom: 20}}
    />}
    
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        uuid: uuid,
        company_name: truckingCompany.company_name,
        is_active: truckingCompany.is_active === 'y' ? true : false
      }}
      onFinish={processSubmit}
    >
      <Form.Item
        label={'Company Name'} 
        name={'company_name'}
        rules={[{ required: true, message: 'Field required'}]}
      >
        <Input 
          placeholder={'Type here'}
          onChange={(e) => setTruckingCompany({
            ...truckingCompany,
            company_name: e.target.value
          })}
        />
      </Form.Item>

      <Form.Item>
        <Switch 
          checked={truckingCompany.is_active === 'y' ? true : false} 
          onChange={(e) => setTruckingCompany({
            ...truckingCompany,
            is_active: e ? 'y' : 'n'
          })}
        />
        <span style={{marginLeft: 15}}>
          Active Status
        </span>
      </Form.Item>

      <Form.Item>
        <Space>
          <Button 
            type={"primary"} 
            htmlType={"submit"}
            loading={isLoading}
          >
            Save
          </Button>
          <Button type={"default"}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>

    {(truckingCompany.uuid) && <>
      <Tabs
        defaultActiveKey={selectedTab||'contacts'}
        onChange={(e:any) => setSelectedTab(e)}
        items={[
          {
            key: 'contacts',
            label: 'Contacts',
            children: <ContactsListing 
              showView
              showLinking
              additionalColumns={[
                {
                  key: 'primary',
                  title: 'Primary',
                  render: (_:any, record:any) => {
                    if (truckingCompany.primary_contact_uuid === record.uuid)
                      return <CheckOutlined style={{color: 'green'}} />
                    else {
                      return <Button 
                        type={'default'}
                        color={'red'}
                        size={'small'}
                        children={'Set Primary'}
                        onClick={() => {
                          setTruckingCompany({
                            ...truckingCompany,
                            primary_contact_uuid: record.uuid
                          });

                          processSubmit({
                            primary_contact_uuid: record.uuid
                          })
                        }}
                      />
                    }
                  }
                }
              ]}
              showTags
              type='trucking-companies'
              typeUuid={truckingCompany.uuid}
            />
          }
        ]}
      />
    </>}
  </BlankPage>
}